import { Component, OnInit, ViewChild } from '@angular/core'
import {
  EntityGroupPlanODataService,
  IEntityGroupPlan,
  IPlan,
  PlanODataService
} from '@exchange/domain/.'
import { ActivatedRoute } from '@angular/router'
import { forkJoin } from 'rxjs'
import { GridWrapperComponent, MultiRowSelection } from 'common-components'

@Component({
  selector: 'app-entity-group-edit-plans',
  templateUrl: './entity-group-edit-plans.component.html',
  standalone: false
})
export class EntityGroupEditPlansComponent implements OnInit {
  @ViewChild('available', { static: true }) available: GridWrapperComponent
  @ViewChild('selected', { static: true }) selected: GridWrapperComponent
  entityGroupId: number = null
  availableSelection: IPlan[] = []
  selectedSelection: IEntityGroupPlan[] = []
  selectedIds: number[] = []

  private _gridOptions = {
    canEdit: false,
    canDelete: false
  }

  constructor(
    private route: ActivatedRoute,
    private planService: PlanODataService,
    private entityGroupPlanService: EntityGroupPlanODataService
  ) {}

  availableCellStyle(data) {
    const style = {}

    if (!this.canSelect(data.value)) {
      style['color'] = 'silver'
    }

    return style
  }

  canSelect(plan: IPlan) {
    return this.selectedIds.indexOf(+plan.Id) === -1
  }

  getSelectedIds() {
    this.entityGroupPlanService
      .query()
      .select('PlanId')
      .filter(`EntityGroupId eq ${this.entityGroupId}`)
      .exec()
      .subscribe(response => {
        this.selectedIds = response.data.map(x => +x.PlanId)
        this.available.api.redrawRows()
      })
  }

  initializeAvailable() {
    this.available.defaultExpand = 'Payer,LineOfBusiness'
    this.available.gridOptions.context = this._gridOptions
    this.available.gridOptions.columnDefs = [
      {
        headerName: 'CHI Plan ID',
        field: 'Id',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'CHI Payer ID',
        field: 'PayerId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'LOB',
        field: 'LineOfBusiness.Value',
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Plan',
        field: 'Name',
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        cellStyle: this.availableCellStyle.bind(this)
      },
      {
        headerName: 'Payer',
        field: 'Payer.Name',
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        cellStyle: this.availableCellStyle.bind(this)
      }
    ]
    this.available.gridOptions.rowSelection = MultiRowSelection
    this.available.odata = this.planService
    this.available.gridOptions.onSelectionChanged = this.recalculateSelected.bind(this)
  }

  initializeSelected() {
    this.selected.defaultFilter = `EntityGroupId eq ${this.entityGroupId}`
    this.selected.gridOptions.context = this._gridOptions
    this.selected.gridOptions.columnDefs = [
      {
        headerName: 'CHI Plan ID',
        field: 'PlanId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'CHI Payer ID',
        field: 'PayerId',
        filter: 'agNumberColumnFilter',
        filterParams: { newRowsAction: 'keep' },
        cellRenderer: GridWrapperComponent.TooltipRenderer()
      },
      {
        headerName: 'LOB',
        field: 'LineOfBusinessName',
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Plan',
        field: 'PlanName',
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      },
      {
        headerName: 'Payer',
        field: 'PayerName',
        width: 200,
        cellRenderer: GridWrapperComponent.TooltipRenderer(),
        filter: 'agTextColumnFilter',
        filterParams: { newRowsAction: 'keep' }
      }
    ]
    this.selected.gridOptions.rowSelection = MultiRowSelection
    this.selected.odata = this.entityGroupPlanService
    this.selected.gridOptions.onSelectionChanged = this.recalculateSelected.bind(this)
  }

  recalculateSelected() {
    this.availableSelection = this.available.api
      .getSelectedRows()
      .filter(this.canSelect.bind(this)) as IPlan[]

    this.selectedSelection = this.selected.api.getSelectedRows() as IEntityGroupPlan[]
  }

  select() {
    // TODO: Implement ODATA Batch
    const observables = this.availableSelection.map(item => {
      const model = {
        EntityGroupId: this.entityGroupId,
        PayerId: item.Payer.Id,
        PayerName: item.Payer.Name,
        PlanId: item.Id,
        PlanName: item.Name,
        LineOfBusinessId: item.LineOfBusiness.Id,
        LineOfBusinessName: item.LineOfBusiness.Value
      } as IEntityGroupPlan

      return this.entityGroupPlanService.post(model).exec()
    })

    forkJoin(observables).subscribe(results => {
      this.selected.getData()
      this.getSelectedIds()
      setTimeout(this.recalculateSelected.bind(this), 250)
    })
  }

  remove() {
    // TODO: Implement ODATA Batch
    const observables = this.selectedSelection.map(item =>
      this.entityGroupPlanService.delete(item.Id).exec()
    )

    forkJoin(observables).subscribe(results => {
      this.selected.getData()
      this.getSelectedIds()
      setTimeout(this.recalculateSelected.bind(this), 250)
    })
  }

  ngOnInit() {
    this.entityGroupId = +this.route.snapshot.params['id']

    this.initializeAvailable()
    this.initializeSelected()
    this.getSelectedIds()
  }
}
